/**
 * Simple safe parsing utilities to prevent React crashes
 */

// Safe date parsing
export interface SafeDateParseResult {
  isValid: boolean;
  date?: Date;
  error?: string;
}

/**
 * Safely parse a date string in various formats without throwing errors
 */
export function safeParseDateValue(
  dateValue: string | null | undefined
): SafeDateParseResult {
  if (!dateValue || typeof dateValue !== "string") {
    return { isValid: false, error: "No date value provided" };
  }

  const trimmedValue = dateValue.trim();
  if (!trimmedValue) {
    return { isValid: false, error: "Empty date value" };
  }

  try {
    // Handle both MM-DD-YYYY and YYYY-MM-DD formats
    if (trimmedValue.includes("-")) {
      const parts = trimmedValue.split("-");
      if (parts.length === 3) {
        const [first, second, third] = parts;

        // Validate that all parts are numeric
        if (
          !first.match(/^\d+$/) ||
          !second.match(/^\d+$/) ||
          !third.match(/^\d+$/)
        ) {
          return { isValid: false, error: "Date parts must be numeric" };
        }

        let parsedDate: Date;

        // Check if it's YYYY-MM-DD (year first)
        if (first.length === 4) {
          const year = parseInt(first, 10);
          const month = parseInt(second, 10);
          const day = parseInt(third, 10);

          // Validate ranges
          if (year < 1900 || year > 2100) {
            return {
              isValid: false,
              error: "Year must be between 1900 and 2100",
            };
          }
          if (month < 1 || month > 12) {
            return { isValid: false, error: "Month must be between 1 and 12" };
          }
          if (day < 1 || day > 31) {
            return { isValid: false, error: "Day must be between 1 and 31" };
          }

          parsedDate = new Date(year, month - 1, day);
        }
        // Otherwise it's MM-DD-YYYY (month first)
        else {
          const month = parseInt(first, 10);
          const day = parseInt(second, 10);
          const year = parseInt(third, 10);

          // Validate ranges
          if (month < 1 || month > 12) {
            return { isValid: false, error: "Month must be between 1 and 12" };
          }
          if (day < 1 || day > 31) {
            return { isValid: false, error: "Day must be between 1 and 31" };
          }
          if (year < 1900 || year > 2100) {
            return {
              isValid: false,
              error: "Year must be between 1900 and 2100",
            };
          }

          parsedDate = new Date(year, month - 1, day);
        }

        // Check if the parsed date is valid (handles invalid dates like Feb 30)
        if (isNaN(parsedDate.getTime())) {
          return { isValid: false, error: "Invalid date" };
        }

        return { isValid: true, date: parsedDate };
      }
    }

    // Try parsing as a general date string as fallback
    const fallbackDate = new Date(trimmedValue);
    if (isNaN(fallbackDate.getTime())) {
      return { isValid: false, error: "Unable to parse date format" };
    }

    return { isValid: true, date: fallbackDate };
  } catch (error) {
    return {
      isValid: false,
      error: error instanceof Error ? error.message : "Unknown parsing error",
    };
  }
}

/**
 * Format date for API (MM-DD-YYYY)
 */
export function safeFormatDateForAPI(
  date: Date | null | undefined
): string | null {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return null;
  }
  try {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  } catch {
    return null;
  }
}

/**
 * Safely convert any value to string
 */
export function safeStringValue(value: any): string {
  if (value == null) return "";
  try {
    return String(value);
  } catch {
    return "";
  }
}

// Safe number parsing
export interface SafeNumberParseResult {
  isValid: boolean;
  value?: number;
  error?: string;
}

/**
 * Safely parse a number from string
 */
export function safeParseNumber(
  value: string | null | undefined
): SafeNumberParseResult {
  if (!value) {
    return { isValid: false, error: "No value" };
  }

  const cleaned = value.replace(/[,$]/g, "");
  const parsed = parseFloat(cleaned);

  if (isNaN(parsed)) {
    return { isValid: false, error: "Not a number" };
  }

  return { isValid: true, value: parsed };
}
